import { useState } from 'react';
import { useAuth } from 'shared/hooks/useAuth';
import { useArenaReservations } from 'modules/arena/reservations/useArenaReservations';
import { StatTile } from 'shared/components/ui/StatTile';
import { PillTabs } from 'shared/components/ui/PillTabs';
import { Button } from 'shared/components/ui/Button';
import { ReservationRow } from 'modules/arena/reservations/components/ReservationRow';
import { ReservationDetailPanel } from 'modules/arena/reservations/components/ReservationDetailPanel';
import { NewReservationForm } from 'modules/arena/reservations/components/NewReservationForm';
import { RESERVATION_STATUS_FILTERS } from 'modules/arena/reservations/content';
import { AsyncSection } from 'shared/components/ui/LoadingState';
import { SkeletonListDetail, SkeletonPills, SkeletonStatGrid } from 'shared/components/ui/Skeleton';

// Recepção: consulta a agenda/reservas, cria reservas em nome de clientes, confirma
// presença (marca no-show/concluída) e cobra pendências — mas não cancela reservas
// pela arena, decisão reservada ao proprietário (ou gestor).
export function RecepcaoReservationsPage() {
  const { user } = useAuth();
  const {
    reservations,
    isLoading,
    error,
    statusFilter,
    setStatusFilter,
    selectedId,
    setSelectedId,
    selectedReservation,
    confirm,
    markNoShow,
    markCompleted,
    extendDeadline,
    chargeRemaining,
    createManualReservation,
  } = useArenaReservations();

  const [isCreating, setIsCreating] = useState(false);

  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-ink">Recepção · Reservas</h1>
          <p className="text-sm text-ink-secondary">{user?.name} · agenda e reservas da unidade.</p>
        </div>
        {!isCreating && <Button onClick={() => setIsCreating(true)}>+ Nova reserva</Button>}
      </div>

      <AsyncSection
        isLoading={isLoading}
        error={error}
        loadingLabel="Carregando reservas..."
        skeleton={
          <>
            <SkeletonStatGrid />
            <SkeletonPills className="mt-6" widths={[64, 96, 176, 112, 104, 104, 88, 80]} />
            <SkeletonListDetail />
          </>
        }
      >
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Pendentes" value={String(reservations.filter((r) => r.status === 'pending').length)} />
            <StatTile
              label="Aguardando pagamento"
              value={String(reservations.filter((r) => r.status === 'awaiting_payment').length)}
            />
            <StatTile
              label="Confirmadas hoje"
              value={String(
                reservations.filter((r) => r.status === 'confirmed' && r.dateLabel.startsWith('hoje')).length
              )}
            />
            <StatTile label="No-show" value={String(reservations.filter((r) => r.status === 'no_show').length)} />
          </div>

          {isCreating && (
            <div className="mt-6">
              <NewReservationForm
                onCreate={(input) => {
                  createManualReservation(input);
                  setIsCreating(false);
                }}
                onCancel={() => setIsCreating(false)}
              />
            </div>
          )}

          <div className="mt-6">
            <PillTabs options={RESERVATION_STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
          </div>

          <div className="mt-4 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            <div className="flex flex-col gap-3">
              {reservations.length === 0 ? (
                <p className="text-sm text-ink-secondary">Nenhuma reserva com esse status.</p>
              ) : (
                reservations.map((reservation) => (
                  <ReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    isSelected={reservation.id === selectedId}
                    onSelect={setSelectedId}
                  />
                ))
              )}
            </div>

            <div className="lg:sticky lg:top-6 lg:self-start">
              {selectedReservation ? (
                <ReservationDetailPanel
                  reservation={selectedReservation}
                  onConfirm={confirm}
                  onCancel={() => {}}
                  onMarkNoShow={markNoShow}
                  onMarkCompleted={markCompleted}
                  onExtendDeadline={extendDeadline}
                  onChargeRemaining={chargeRemaining}
                  canCancel={false}
                />
              ) : (
                <p className="text-sm text-ink-secondary">Selecione uma reserva para ver os detalhes.</p>
              )}
            </div>
          </div>
      </AsyncSection>
    </section>
  );
}
